/**
 * galleryBlockModel — o bloco Galeria (blockType 8) como dado: operações PURAS sobre o `config` que vai para a página e
 * a tradução desse config no ViewModel do contrato `galleryBlock.vm.js` @v1.
 *
 * Formato do `config` (o mesmo que a Api valida — SiteConfigValidator, TODO 3.4 — e o kit desenha — renderGallery):
 *   { layout: 0|1 (GalleryLayout), items: [{ assetId, origem, w, h, color, caption?, alt? }], images: [{ id, url, caption }] }
 * `items` é o formato da biblioteca; `images` é o antigo (endereço copiado), que continua sendo desenhado DEPOIS.
 */
import { GalleryLayout, MediaOrigin } from '../../../catalogs/pageCatalog.generated.js';
import { MAX_FOTOS_POR_GALERIA, MAX_DESCRICAO } from '../../media/mediaLibraryVmBuilder.js';

export const MAX_LEGENDA = 140;   // SiteConfigValidator.MaxLegenda

const IMAGES_CDN_URL = (import.meta.env?.VITE_IMAGES_CDN_URL || 'https://images.agendario.com.br').replace(/\/$/, '');

/**
 * Pasta da imagem na CDN — a MESMA convenção do FileService (finalidades.json), da Mídia (`Enderecos.Pasta`) e do kit
 * das páginas (`pastaDaImagem`, P6). Se mudar lá, muda aqui.
 */
export function pastaDaImagem(assetId, origem, donoId) {
  if (!assetId) return null;
  if (origem === MediaOrigin.Shared) return `${IMAGES_CDN_URL}/shared/media/${assetId}/`;
  return donoId ? `${IMAGES_CDN_URL}/${donoId}/media/${assetId}/` : null;
}

export function normalizar(config) {
  return {
    ...config,
    layout: config?.layout === GalleryLayout.Carousel ? GalleryLayout.Carousel : GalleryLayout.Grid,
    items: Array.isArray(config?.items) ? config.items : [],
    images: Array.isArray(config?.images) ? config.images : [],
  };
}

const total = (c) => c.items.length + c.images.length;
export const vagas = (config) => Math.max(0, MAX_FOTOS_POR_GALERIA - total(normalizar(config)));

/** Fotos escolhidas na biblioteca (na ordem) entram no FIM da galeria; repetidas e o que passar de 30 ficam de fora. */
export function adicionar(config, fotos) {
  const c = normalizar(config);
  const ja = new Set(c.items.map((i) => i.assetId));
  const novas = [];
  for (const f of fotos ?? []) {
    if (ja.has(f.assetId) || total(c) + novas.length >= MAX_FOTOS_POR_GALERIA) continue;
    ja.add(f.assetId);
    const item = { assetId: f.assetId, origem: f.origem, w: f.w, h: f.h };
    if (f.color) item.color = f.color;
    if (f.alt) item.alt = f.alt.slice(0, MAX_DESCRICAO);
    novas.push(item);
  }
  return { ...c, items: [...c.items, ...novas] };
}

// Chave do item na tela: assetId (não se repete na galeria) ou o id do item antigo (com o índice de reserva).
const chaveDoItem = (i) => `lib:${i.assetId}`;
const chaveDoAntigo = (img, indice) => `old:${img?.id ?? indice}`;

function mudarItem(config, key, mudar) {
  const c = normalizar(config);
  return {
    ...c,
    items: c.items.map((i) => (chaveDoItem(i) === key ? mudar(i) : i)),
    images: c.images.map((img, n) => (chaveDoAntigo(img, n) === key ? mudar(img) : img)),
  };
}

export const legenda = (config, key, texto) => mudarItem(config, key, (i) => ({ ...i, caption: String(texto ?? '').slice(0, MAX_LEGENDA) }));

export function descricao(config, key, texto) {
  return mudarItem(config, key, (i) => {
    if (!key.startsWith('lib:')) return i;   // item antigo não tem descrição
    const t = String(texto ?? '').slice(0, MAX_DESCRICAO);
    const { alt: _anterior, ...resto } = i;
    return t.trim() ? { ...resto, alt: t } : resto;
  });
}

export function remover(config, key) {
  const c = normalizar(config);
  return {
    ...c,
    items: c.items.filter((i) => chaveDoItem(i) !== key),
    images: c.images.filter((img, n) => chaveDoAntigo(img, n) !== key),
  };
}

/** Reordena entre os itens da biblioteca (os antigos ficam sempre depois, na ordem deles). */
export function reordenar(config, de, para) {
  const c = normalizar(config);
  if (de === para || de < 0 || para < 0 || de >= c.items.length || para >= c.items.length) return c;
  const items = [...c.items];
  const [movido] = items.splice(de, 1);
  items.splice(para, 0, movido);
  return { ...c, items };
}

export function mover(config, key, delta) {
  const c = normalizar(config);
  const de = c.items.findIndex((i) => chaveDoItem(i) === key);
  return de < 0 ? c : reordenar(c, de, de + delta);
}

export const trocarLayout = (config, valor) => ({
  ...normalizar(config),
  layout: valor === GalleryLayout.Carousel ? GalleryLayout.Carousel : GalleryLayout.Grid,
});

/** assetIds que a galeria já tem — a biblioteca não deixa escolher de novo. */
export const jaNaGaleria = (config) => normalizar(config).items.map((i) => i.assetId);

/**
 * ViewModel do editor do bloco.
 * @param {object} config  o config do bloco
 * @param {string|null} donoId  usuário logado (foto do dono mora em `{dono}/media/{id}/`)
 */
export function montarVm(config, donoId) {
  const c = normalizar(config);
  const n = total(c);
  const daBiblioteca = c.items.map((i, indice) => {
    const pasta = pastaDaImagem(i.assetId, i.origem, donoId);
    const caption = i.caption ?? '';
    return {
      key: chaveDoItem(i),
      kind: 'library',
      thumbnailUrl: pasta ? `${pasta}w320.webp` : null,
      aspectRatio: i.w > 0 && i.h > 0 ? i.w / i.h : null,
      color: i.color ?? null,
      badgeLabel: i.origem === MediaOrigin.Shared ? 'Exemplo' : null,
      noticeLabel: null,
      noticeTone: 'neutral',
      positionLabel: `Foto ${indice + 1} de ${n}`,
      caption,
      captionMaxLength: MAX_LEGENDA,
      captionCounterLabel: `${caption.length}/${MAX_LEGENDA}`,
      alt: i.alt ?? '',
      altMaxLength: MAX_DESCRICAO,
      altPlaceholder: 'Descreva a foto para quem usa leitor de tela (opcional)',
      canMoveUp: indice > 0,
      canMoveDown: indice < c.items.length - 1,
      removeLabel: 'Tirar da página',
    };
  });
  const antigos = c.images.map((img, indice) => {
    const caption = img?.caption ?? '';
    const temImagem = typeof img?.url === 'string' && img.url.length > 0;
    return {
      key: chaveDoAntigo(img, indice),
      kind: 'legacy',
      thumbnailUrl: temImagem ? img.url : null,
      aspectRatio: null,
      color: null,
      badgeLabel: null,
      noticeLabel: temImagem
        ? 'Imagem antiga — para a página ficar mais leve, troque por uma da biblioteca.'
        : 'Sem imagem — remova este item.',
      noticeTone: temImagem ? 'neutral' : 'warning',
      positionLabel: `Foto ${c.items.length + indice + 1} de ${n}`,
      caption,
      captionMaxLength: MAX_LEGENDA,
      captionCounterLabel: `${caption.length}/${MAX_LEGENDA}`,
      alt: null,
      altMaxLength: MAX_DESCRICAO,
      altPlaceholder: 'Descreva a foto para quem usa leitor de tela (opcional)',
      canMoveUp: false,
      canMoveDown: false,
      removeLabel: 'Tirar da página',
    };
  });
  const cheio = n >= MAX_FOTOS_POR_GALERIA;
  return {
    layout: {
      value: c.layout,
      options: [
        { value: GalleryLayout.Grid, label: 'Grade', hint: 'Todas as fotos à vista, lado a lado.' },
        { value: GalleryLayout.Carousel, label: 'Carrossel', hint: 'Uma foto grande por vez — o visitante arrasta para o lado.' },
      ],
    },
    items: [...daBiblioteca, ...antigos],
    countLabel: n === 0 ? 'Nenhuma foto' : `${n} de ${MAX_FOTOS_POR_GALERIA} fotos`,
    canAdd: !cheio,
    addLabel: 'Adicionar fotos',
    limitMessage: cheio ? `Esta galeria chegou a ${MAX_FOTOS_POR_GALERIA} fotos, o limite.` : null,
    empty: n === 0,
    emptyTitle: 'Sua galeria está vazia',
    emptyMessage: 'Escolha fotos suas ou exemplos do seu ramo — dá para trocar depois.',
    hint: 'Dica: 6 a 12 fotos boas valem mais que muitas — a página fica leve no celular.',
  };
}
